import React from "react";
import { Tabs, Tab, Form, Row, Col } from "react-bootstrap";

interface AdvancedFiltersProps {
  lookups: any;
  searchParams: any;
  onParamChange: (key: string, value: any) => void;
}

const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  lookups,
  searchParams,
  onParamChange,
}) => {
  /* Shared style tokens — applied to every label / control in this file so
     the panel matches the modernised toolbar and results table (uppercase
     slate label, 34px control band, slate-300 border, 6px radius). Inline
     styles override Bootstrap's `.form-control` cascade so we don't need
     any external CSS overrides. */
  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "0.68rem",
    fontWeight: 700,
    color: "var(--text-body)",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: 4,
    lineHeight: 1.25,
  };
  const inputStyle: React.CSSProperties = {
    height: 34,
    padding: "0 10px",
    fontSize: "0.82rem",
    borderRadius: 6,
    border: "1px solid var(--border-strong)",
    background: "var(--surface-card)",
    color: "var(--text-strong)",
    width: "100%",
    boxSizing: "border-box",
  };

  return (
    <div
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-default)",
        borderRadius: 12,
        marginBottom: 12,
        padding: 16,
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      }}
    >
      {/* Local CSS for the Tabs nav + every <select> in this panel.
          Reach into react-bootstrap's class hooks so the tabs use a soft
          pill style and the native <select> arrows pick up the slate
          border treatment without us having to wrap every Form.Control.
          Scope is `.ts-advanced-tabs` so it can't leak into other pages. */}
      <style>{`
        .ts-advanced-tabs.nav-tabs {
          border-bottom: 1px solid #e2e8f0;
          gap: 4px;
        }
        .ts-advanced-tabs.nav-tabs .nav-link {
          border: 1px solid transparent;
          border-radius: 8px 8px 0 0;
          padding: 6px 14px;
          color: #64748b;
          font-size: 0.85rem;
          transition: background-color .12s, color .12s;
        }
        .ts-advanced-tabs.nav-tabs .nav-link:hover {
          color: #0f172a;
          background: #f8fafc;
          border-color: transparent;
        }
        .ts-advanced-tabs.nav-tabs .nav-link.active {
          color: #1d4ed8;
          background: #eff6ff;
          border-color: #e2e8f0 #e2e8f0 #fff;
          font-weight: 600;
        }
        .ts-advanced-tabs ~ .tab-content .form-control,
        .ts-advanced-tabs ~ .tab-content select.form-control,
        .ts-advanced-tabs ~ .tab-content select,
        .ts-advanced-tabs ~ .tab-content input[type="date"] {
          height: 34px;
          padding: 0 10px;
          font-size: 0.82rem;
          border-radius: 6px;
          border: 1px solid #cbd5e1;
          color: #0f172a;
          box-shadow: none;
        }
        .ts-advanced-tabs ~ .tab-content .form-control:focus,
        .ts-advanced-tabs ~ .tab-content select:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.15);
          outline: none;
        }
        .ts-advanced-tabs ~ .tab-content .form-check-input:checked {
          background-color: #2563eb;
          border-color: #2563eb;
        }
      `}</style>
      <Tabs
        defaultActiveKey="advanced"
        id="advanced-filter-tabs"
        className="mb-3 ts-advanced-tabs"
      >
        {/* ── Advanced Options Tab ── */}
        <Tab eventKey="advanced" title={<i className="fa fa-cogs" title="Advanced Options" />}>
          <Row className="g-2">
            <Col md={2}>
              <Form.Label style={labelStyle}>From Enquiry ID</Form.Label>
              <Form.Control size="sm" type="number" style={inputStyle}
                value={searchParams.fromEnquiryId || ""}
                onChange={(e) => onParamChange("fromEnquiryId", e.target.value ? Number(e.target.value) : undefined)}
              />
            </Col>
            <Col md={2}>
              <Form.Label style={labelStyle}>To Enquiry ID</Form.Label>
              <Form.Control size="sm" type="number" style={inputStyle}
                value={searchParams.toEnquiryId || ""}
                onChange={(e) => onParamChange("toEnquiryId", e.target.value ? Number(e.target.value) : undefined)}
              />
            </Col>
            <Col md={2}>
              <Form.Label style={labelStyle}>Name</Form.Label>
              <Form.Control size="sm" type="text" style={inputStyle}
                value={searchParams.name || ""}
                onChange={(e) => onParamChange("name", e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label style={labelStyle}>Phone No</Form.Label>
              <Form.Control size="sm" type="text" style={inputStyle}
                value={searchParams.phoneNo || ""}
                onChange={(e) => onParamChange("phoneNo", e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label style={labelStyle}>Email</Form.Label>
              <Form.Control size="sm" type="text" style={inputStyle}
                value={searchParams.emailAddress || ""}
                onChange={(e) => onParamChange("emailAddress", e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label style={labelStyle}>Form Number</Form.Label>
              <Form.Control size="sm" type="text" style={inputStyle}
                value={searchParams.enquiryFormNumber || ""}
                onChange={(e) => onParamChange("enquiryFormNumber", e.target.value)}
              />
            </Col>
          </Row>
          <Row className="g-2 mt-1">
            <Col md={2}>
              <Form.Label style={labelStyle}>Receipt Number</Form.Label>
              <Form.Control size="sm" type="text" style={inputStyle}
                value={searchParams.receiptNumber || ""}
                onChange={(e) => onParamChange("receiptNumber", e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label style={labelStyle}>Cheque No</Form.Label>
              <Form.Control size="sm" type="text" style={inputStyle}
                value={searchParams.chequeNo || ""}
                onChange={(e) => onParamChange("chequeNo", e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label style={labelStyle}>Enquiry Mode</Form.Label>
              <Form.Select size="sm" style={inputStyle}
                value={searchParams.enquiryModeId || ""}
                onChange={(e) => onParamChange("enquiryModeId", e.target.value ? Number(e.target.value) : undefined)}
              >
                <option value="">-- Select --</option>
                {lookups?.enquiryModes?.map((m: any) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label style={labelStyle}>Webform</Form.Label>
              <Form.Select size="sm" style={inputStyle}
                value={searchParams.webformId || ""}
                onChange={(e) => onParamChange("webformId", e.target.value ? Number(e.target.value) : undefined)}
              >
                <option value="">-- Select --</option>
                {lookups?.webforms?.map((w: any) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label style={labelStyle}>Sort By</Form.Label>
              <Form.Select size="sm" style={inputStyle}
                value={searchParams.sortBy || "1"}
                onChange={(e) => onParamChange("sortBy", Number(e.target.value))}
              >
                <option value="1">Enquiry ID (Desc)</option>
                <option value="2">First Name</option>
                <option value="3">Student Code</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label style={labelStyle}>Photo Status</Form.Label>
              <Form.Select size="sm" style={inputStyle}
                value={searchParams.photoUploadedStatus || ""}
                onChange={(e) => onParamChange("photoUploadedStatus", e.target.value ? Number(e.target.value) : undefined)}
              >
                <option value="">-- All --</option>
                <option value="5">Not Uploaded</option>
                <option value="6">Uploaded</option>
              </Form.Select>
            </Col>
          </Row>
          <Row className="g-2 mt-1">
            <Col md={2}>
              <Form.Label style={labelStyle}>Joined Year</Form.Label>
              <Form.Select size="sm" style={inputStyle}
                value={searchParams.joinedYearId || ""}
                onChange={(e) => onParamChange("joinedYearId", e.target.value ? Number(e.target.value) : undefined)}
              >
                <option value="">-- Select --</option>
                {lookups?.years?.map((y: any) => (
                  <option key={y.id} value={y.id}>{y.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label style={labelStyle}>Batch Status</Form.Label>
              <Form.Select size="sm" style={inputStyle}
                value={searchParams.batchStatus || ""}
                onChange={(e) => onParamChange("batchStatus", e.target.value)}
              >
                <option value="">-- All --</option>
                <option value="1">Active</option>
                <option value="2">Inactive</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label style={labelStyle}>Admission No</Form.Label>
              <Form.Control size="sm" type="text" style={inputStyle}
                value={searchParams.admissionNo || ""}
                onChange={(e) => onParamChange("admissionNo", e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label style={labelStyle}>Potential Join From</Form.Label>
              <Form.Control size="sm" type="date" style={inputStyle}
                value={searchParams.potentialJoinDateFrom || ""}
                onChange={(e) => onParamChange("potentialJoinDateFrom", e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label style={labelStyle}>Potential Join To</Form.Label>
              <Form.Control size="sm" type="date" style={inputStyle}
                value={searchParams.potentialJoinDateTo || ""}
                onChange={(e) => onParamChange("potentialJoinDateTo", e.target.value)}
              />
            </Col>
            <Col md={2} style={{ display: "flex", alignItems: "flex-end", paddingBottom: 4 }}>
              <Form.Check
                type="checkbox"
                label={<span style={{ fontSize: 12 }}>No Follow-up</span>}
                checked={searchParams.noFollowup || false}
                onChange={(e) => onParamChange("noFollowup", e.target.checked)}
              />
            </Col>
          </Row>
        </Tab>

        {/* ── History Tab ── */}
        <Tab eventKey="history" title={<i className="fa fa-history" title="History Search" />}>
          <Row className="g-2">
            <Col md={3}>
              <Form.Label style={labelStyle}>From Date</Form.Label>
              <Form.Control size="sm" type="date" style={inputStyle}
                value={searchParams.historyFromDate || ""}
                onChange={(e) => onParamChange("historyFromDate", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label style={labelStyle}>To Date</Form.Label>
              <Form.Control size="sm" type="date" style={inputStyle}
                value={searchParams.historyToDate || ""}
                onChange={(e) => onParamChange("historyToDate", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label style={labelStyle}>User</Form.Label>
              <Form.Select size="sm" style={inputStyle}
                value={searchParams.userId || ""}
                onChange={(e) => onParamChange("userId", e.target.value)}
              >
                <option value="">-- All Users --</option>
                {lookups?.followupUsers?.map((u: any) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          <Row className="g-2 mt-2">
            <Col md={3}>
              <Form.Check type="checkbox" label={<span style={{ fontSize: 12 }}>Enquiry Creation Date</span>}
                checked={searchParams.enquiryCreationDate || false}
                onChange={(e) => onParamChange("enquiryCreationDate", e.target.checked)}
              />
            </Col>
            <Col md={3}>
              <Form.Check type="checkbox" label={<span style={{ fontSize: 12 }}>Enquiry Last Modified Date</span>}
                checked={searchParams.enquiryLastModifiedDate || false}
                onChange={(e) => onParamChange("enquiryLastModifiedDate", e.target.checked)}
              />
            </Col>
            <Col md={3}>
              <Form.Check type="checkbox" label={<span style={{ fontSize: 12 }}>Enquiry Date</span>}
                checked={searchParams.enquiryDate || false}
                onChange={(e) => onParamChange("enquiryDate", e.target.checked)}
              />
            </Col>
            <Col md={3}>
              <Form.Check type="checkbox" label={<span style={{ fontSize: 12 }}>Registration Date</span>}
                checked={searchParams.registrationDate || false}
                onChange={(e) => onParamChange("registrationDate", e.target.checked)}
              />
            </Col>
          </Row>
        </Tab>

        {/* ── Registration Test Tab ── */}
        <Tab eventKey="registration" title={<i className="fa fa-registered" title="Registration Test" />}>
          <Row className="g-2">
            <Col md={3}>
              <Form.Label style={labelStyle}>Hall Ticket Number</Form.Label>
              <Form.Control size="sm" type="text" style={inputStyle}
                value={searchParams.hallTicketNumber || ""}
                onChange={(e) => onParamChange("hallTicketNumber", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label style={labelStyle}>Test Register Date</Form.Label>
              <Form.Control size="sm" type="date" style={inputStyle}
                value={searchParams.testRegisterDate || ""}
                onChange={(e) => onParamChange("testRegisterDate", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label style={labelStyle}>Centre</Form.Label>
              <Form.Select size="sm" style={inputStyle}
                value={searchParams.registrationTestCentre || ""}
                onChange={(e) => onParamChange("registrationTestCentre", e.target.value)}
              >
                <option value="">-- Select --</option>
                {lookups?.centres?.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} style={{ display: "flex", alignItems: "flex-end", paddingBottom: 4 }}>
              <Form.Check type="checkbox"
                label={<span style={{ fontSize: 12 }}>Enable Registration Search</span>}
                checked={searchParams.registrationSearchCheck || false}
                onChange={(e) => onParamChange("registrationSearchCheck", e.target.checked)}
              />
            </Col>
          </Row>
        </Tab>

        {/* ── Tags Tab ── */}
        <Tab eventKey="tags" title={<i className="fa fa-tags" title="Tag Search" />}>
          <Row className="g-2">
            <Col md={3}>
              <Form.Label style={labelStyle}>Enrollment Tag Type</Form.Label>
              <Form.Select size="sm" style={inputStyle}
                value={searchParams.tagType || ""}
                onChange={(e) => {
                  onParamChange("tagType", e.target.value);
                  onParamChange("tagId", "");
                }}
              >
                <option value="">-- None --</option>
                {lookups?.studentTagTypes?.map((tt: any) => (
                  <option key={tt.id} value={tt.id}>{tt.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label style={labelStyle}>Enrollment Tag</Form.Label>
              <Form.Select size="sm" style={inputStyle}
                value={searchParams.tagId || ""}
                onChange={(e) => onParamChange("tagId", e.target.value)}
              >
                <option value="">-- None --</option>
                {lookups?.studentTagTypes
                  ?.find((tt: any) => String(tt.id) === String(searchParams.tagType))
                  ?.tags?.map((t: any) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label style={labelStyle}>Enquiry Tag Type</Form.Label>
              <Form.Select size="sm" style={inputStyle}
                value={searchParams.enquiryTagType || ""}
                onChange={(e) => {
                  onParamChange("enquiryTagType", e.target.value);
                  onParamChange("enquiryTagId", "");
                }}
              >
                <option value="">-- None --</option>
                {lookups?.enquiryTagTypes?.map((tt: any) => (
                  <option key={tt.id} value={tt.id}>{tt.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label style={labelStyle}>Enquiry Tag</Form.Label>
              <Form.Select size="sm" style={inputStyle}
                value={searchParams.enquiryTagId || ""}
                onChange={(e) => onParamChange("enquiryTagId", e.target.value)}
              >
                <option value="">-- None --</option>
                {lookups?.enquiryTagTypes
                  ?.find((tt: any) => String(tt.id) === String(searchParams.enquiryTagType))
                  ?.tags?.map((t: any) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
              </Form.Select>
            </Col>
          </Row>
          <Row className="g-2 mt-1">
            <Col md={3}>
              <Form.Label style={labelStyle}>Batch Tag Type</Form.Label>
              <Form.Select size="sm" style={inputStyle}
                value={searchParams.batchTagType || ""}
                onChange={(e) => {
                  onParamChange("batchTagType", e.target.value);
                  onParamChange("batchTagId", "");
                }}
              >
                <option value="">-- None --</option>
                {lookups?.batchTagTypes?.map((tt: any) => (
                  <option key={tt.id} value={tt.id}>{tt.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label style={labelStyle}>Batch Tag</Form.Label>
              <Form.Select size="sm" style={inputStyle}
                value={searchParams.batchTagId || ""}
                onChange={(e) => onParamChange("batchTagId", e.target.value)}
              >
                <option value="">-- None --</option>
                {lookups?.batchTagTypes
                  ?.find((tt: any) => String(tt.id) === String(searchParams.batchTagType))
                  ?.tags?.map((t: any) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
              </Form.Select>
            </Col>
            <Col md={3} style={{ display: "flex", alignItems: "flex-end", paddingBottom: 4 }}>
              <Form.Check type="checkbox"
                label={<span style={{ fontSize: 12 }}>Enable Tag Search</span>}
                checked={searchParams.isTagTypeSearch || false}
                onChange={(e) => onParamChange("isTagTypeSearch", e.target.checked)}
              />
            </Col>
          </Row>
        </Tab>

        {/* ── Attendance Tab ── */}
        <Tab eventKey="attendance" title={<i className="fa fa-id-badge" title="Attendance Search" />}>
          <Row className="g-2">
            <Col md={3}>
              <Form.Label style={labelStyle}>Attendance Date</Form.Label>
              <Form.Control size="sm" type="date" style={inputStyle}
                value={searchParams.attendanceDate || ""}
                onChange={(e) => onParamChange("attendanceDate", e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label style={labelStyle}>Attendance Status</Form.Label>
              <Form.Select size="sm" style={inputStyle}
                value={searchParams.attendanceStatus || ""}
                onChange={(e) => onParamChange("attendanceStatus", e.target.value)}
              >
                <option value="">-- All --</option>
                <option value="1">Present</option>
                <option value="0">Absent</option>
              </Form.Select>
            </Col>
          </Row>
        </Tab>

        {/* ── Material Tab ── */}
        <Tab eventKey="material" title={<i className="fa fa-list-alt" title="Material Search" />}>
          <div style={{ padding: 20, color: "var(--text-faint)", fontSize: 13 }}>
            Material search filters — coming soon.
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};

export default AdvancedFilters;
